import Map from 'ol/Map';
import { useCallback, useRef, useState } from 'react';
import { IMapWidget, MapWidgetSelectionShape } from '../IMapWidget';

import { RelationEditor } from '@framework/form';
import { Feature, MapBrowserEvent } from 'ol';
import { Draw, Modify, Select, Snap } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { MapGeometryItem } from '../createMapSchema';

import { last } from 'lodash';
import { GeoJSON } from 'ol/format';
import { Geometry } from 'ol/geom';

const geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

interface IMapDrawingInteractionsOptions {
  widget: IMapWidget;
  geometriesEditor: RelationEditor<MapGeometryItem>;
}

/**
 * Gestisce le interazioni di disegno sulla mappa, come il disegno
 * di geometrie e l'eliminazione di geometrie disegnate precedentemente.
 */
export function useMapDrawingInteractions(
  options: IMapDrawingInteractionsOptions,
) {
  const { widget, geometriesEditor } = options;

  const source = useRef(new VectorSource({ wrapX: false }));
  const interactions = useRef<{
    draw?: Draw;
    snap?: Snap;
    modify?: Modify;
    remove?: Select;
  }>({});

  const [mode, setMode] = useState('view' as 'view' | 'draw' | 'delete');
  const modeRef = useRef(mode);

  /**
   * Aggiorna la modalità di disegno in base alla selezione dell'utente.
   */
  const handleModeChange = useCallback(
    (nextMode: 'view' | 'draw' | 'delete') => {
      setMode(nextMode);
      modeRef.current = nextMode; // Necessario per le closure di OpenLayers

      const { draw, snap, modify, remove } = interactions.current;
      draw?.setActive(false);
      snap?.setActive(false);
      modify?.setActive(false);
      remove?.setActive(false);

      switch (nextMode) {
        case 'view':
          break;
        case 'draw':
          draw?.setActive(true);
          snap?.setActive(true);
          modify?.setActive(true);
          break;
        case 'delete':
          remove?.setActive(true);
          break;
      }
    },
    [setMode],
  );

  const initDrawing = useCallback(
    (map: Map | null) => {
      if (!map) return;

      const vectorLayer = new VectorLayer({
        source: source.current,
      });
      map.addLayer(vectorLayer);

      const initialFeatures = geometriesEditor.items
        .map(i => {
          try {
            const feature = geoJSON.readFeature(i.feature) as Feature<Geometry>;
            feature.setId(i.id);
            return feature;
          } catch (e) {
            console.error('Errore nella conversione della Feature GeoJSON:', e);
            return null;
          }
        })
        .filter(f => f !== null) as Array<Feature<Geometry>>; // Rimuovo i null

      if (initialFeatures.length > 0) {
        source.current.addFeatures(initialFeatures);
        map
          .getView()
          .fit(source.current.getExtent(), { padding: [16, 16, 16, 16] });
      }

      // Modifica
      const modify = new Modify({ source: source.current });
      map.addInteraction(modify);
      interactions.current.modify = modify;

      const draw = new Draw({
        source: source.current,
        type:
          widget.selection?.shape == MapWidgetSelectionShape.Point
            ? 'Point'
            : 'Polygon',
      });
      map.addInteraction(draw);
      interactions.current.draw = draw;

      const snap = new Snap({ source: source.current });
      map.addInteraction(snap);
      interactions.current.snap = snap;

      const remove = new Select({
        layers: [vectorLayer],
        style: new Style({
          fill: new Fill({
            color: '#eeeeee',
          }),
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 0.7)',
            width: 2,
          }),
        }),
      });
      map.addInteraction(remove);
      interactions.current.remove = remove;

      const hoveredFeatures = new Set<Feature<Geometry>>();

      map.on('pointermove', function (this: Map, e: MapBrowserEvent<any>) {
        if (modeRef.current !== 'delete') return;

        // Reset style
        if (hoveredFeatures.size > 0) {
          hoveredFeatures.forEach(f => {
            f.setStyle();
          });
          hoveredFeatures.clear();
        }

        // Evidenzia la feature sotto il cursore. Selezione solo l'ultima
        // che rappresenta la feature più in alto (è lo stesso comportamento
        // di OpenLayers per la selezione delle feature)
        const hit = source.current.getFeaturesAtCoordinate(e.coordinate);
        const hitTarget = last(hit);
        if (hitTarget != null) {
          hitTarget.setStyle(
            new Style({
              stroke: new Stroke({ color: 'red', width: 2 }),
              fill: new Fill({ color: 'rgba(255, 0, 0, 0.6)' }),
            }),
          );
          hoveredFeatures.add(hitTarget);
          this.getTargetElement().style.cursor = 'not-allowed';
        } else {
          this.getTargetElement().style.cursor = '';
        }
      });

      handleModeChange('view');
    },
    [geometriesEditor.items, handleModeChange],
  );

  return {
    interactions,
    featuresSource: source,
    mode,
    initDrawing,
    handleModeChange,
  };
}
